import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  MEDIA_RESOURCE_TYPES,
  MEDIA_TYPES,
  RESOLUTIONS,
  STREAMING_PROTOCOLS,
  type MediaResourceType,
  type MediaType,
  type Resolution,
  type StreamingProtocol,
} from "@shared/types/content-api";
import {
  emptyMediaDraft,
  emptyResourceDraft,
  type MediaDraft,
  type MediaResourceDraft,
} from "@shared/asset-draft";
import { preferredPreviewResource } from "@shared/stream-preview";
import { tokens } from "../../theme";
import { CollectionRow, Field, StringListField } from "./editorBits";
import StreamPreview from "./StreamPreview";

/**
 * `medias[] → mediaResources[]` - the real shape, edited as it really is
 * (`BE-4`, re-pointed by `BE-15`).
 *
 * ## What this replaces, and why the flat version could not stay
 *
 * The shipped mockup put `width`, `height`, `audioLanguages`,
 * `subtitleLanguages` and ONE resource list on the channel itself. Upstream
 * every one of those lives on a **media**, of which an asset has several, and
 * that costs the flat model two things it can never say:
 *
 *  - **a trailer is inexpressible.** A main feature and its trailer are two
 *    medias on one asset, differing in type, duration and often language.
 *  - **a 4K main and an HD fallback collapse** into one width and one height,
 *    which is then wrong for at least one of them.
 *
 * A channel normally has exactly one media (`type: "channel"`), which is what
 * made the flattening look harmless right up until a second rendition appeared.
 * The routes now refuse a body in the old shape by name rather than dropping
 * the fields, because a silently dropped `resources` array saves a row that
 * plays nothing behind a green tick.
 *
 * ## DRM is one boolean here, and that is not an oversight
 *
 * Production does not store licence URLs; it **mints them per playback
 * request** (an ES256 Verimatrix JWT with a 30-minute life, from the asset's
 * `alternativeId`). There is no scheme list, no licence URL and no certificate
 * to author - `BE-4` shipped all three and none of them had an upstream home.
 * The two authoring-time checks that fall out of it are in
 * `assetPlaybackBlockers`, and the dialog draws them.
 *
 * ## The units are in the labels, on purpose
 *
 * `durationMillis` is MILLISECONDS and the asset-level `duration` beside it is
 * SECONDS. The portal's own Duration box is free text with no unit label at all
 * feeding an `Int` column, which is why the catalogue is assumed to hold mixed
 * units until somebody counts them.
 */

const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  main: "Main",
  trailer: "Trailer",
  clip: "Clip",
  channel: "Channel feed",
  other: "Other",
};

const RESOURCE_TYPE_LABELS: Record<MediaResourceType, string> = {
  native: "Native",
  external: "External",
};

const PROTOCOL_LABELS: Record<StreamingProtocol, string> = {
  hls: "HLS",
  dash: "DASH",
  other: "Other (unplayable)",
};

function ResourceRow({
  resource,
  index,
  siblings,
  onChange,
  onRemove,
}: {
  resource: MediaResourceDraft;
  index: number;
  /** Every resource on this media, so a refusal can name a better one. */
  siblings: readonly MediaResourceDraft[];
  onChange: (patch: Partial<MediaResourceDraft>) => void;
  onRemove: () => void;
}) {
  return (
    <Stack
      spacing={1}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: tokens.brandTintSoft,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "flex-start" }}
      >
        <Field
          select
          label="Delivery"
          value={resource.type}
          onChange={(e) =>
            onChange({ type: e.target.value as MediaResourceType })
          }
          sx={{ width: 140 }}
        >
          {MEDIA_RESOURCE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {RESOURCE_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </Field>
        <Field
          select
          label="Protocol"
          value={resource.protocol}
          onChange={(e) =>
            onChange({ protocol: e.target.value as StreamingProtocol })
          }
          sx={{ width: 180 }}
          helperText={
            resource.protocol === "other"
              ? "The catalogue declines to say what this is, so no player can select it."
              : undefined
          }
        >
          {STREAMING_PROTOCOLS.map((protocol) => (
            <MenuItem key={protocol} value={protocol}>
              {PROTOCOL_LABELS[protocol]}
            </MenuItem>
          ))}
        </Field>
        <Field
          label="Stream URL"
          placeholder="https://cdn.example.com/live.m3u8"
          value={resource.url}
          onChange={(e) => onChange({ url: e.target.value })}
          sx={{ flex: "1 1 auto", minWidth: 220 }}
          inputProps={{ "data-testid": `resource-url-${index}` }}
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={resource.isEncrypted}
              onChange={(e) => onChange({ isEncrypted: e.target.checked })}
            />
          }
          label={<Typography sx={{ fontSize: 12 }}>Encrypted</Typography>}
        />
        <Button
          size="small"
          onClick={onRemove}
          sx={{ color: tokens.error, mt: 1, minHeight: 0 }}
        >
          Remove
        </Button>
      </Stack>

      {/* Under the fields rather than beside them: the player is 16:9 and the
          row is already five controls wide. Every other verdict in this dialog
          is derived from metadata — this is the one place the editor can show
          that a URL leads to actual video. */}
      <StreamPreview resource={resource} siblings={siblings} />
    </Stack>
  );
}

/**
 * The media's own preview: the one an operator reaches for.
 *
 * `preferredPreviewResource` picks the rendition the estate actually plays —
 * a clear HLS first, then anything else playable, then the first row so the
 * refusal shown is about something real rather than about an empty list.
 *
 * A media with no streams at all draws a sentence instead of a dead button,
 * because in the catalogue this CMS is bound to that is the COMMON case, and a
 * missing control reads as a missing feature.
 */
function MediaPreviewHeader({ media }: { media: MediaDraft }) {
  // Native HLS is a browser fact the player probes for itself; assuming "no"
  // here only affects WHICH resource is preferred, and hls.js plays everything
  // native HLS would.
  const chosen = preferredPreviewResource(media.mediaResources, false);
  if (!chosen) {
    return (
      <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
        No stream on this media yet, so there is nothing to preview. Add one
        below and the preview appears.
      </Typography>
    );
  }
  return <StreamPreview resource={chosen} siblings={media.mediaResources} />;
}

export default function MediasEditor({
  medias,
  defaultType,
  onChange,
}: {
  medias: MediaDraft[];
  /** `channel` for a channel, `main` for a title - the one asset-shaped choice. */
  defaultType: MediaType;
  onChange: (next: MediaDraft[]) => void;
}) {
  function update(index: number, patch: Partial<MediaDraft>) {
    onChange(medias.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  }

  return (
    <Stack spacing={2}>
      {/* Says the quiet part, because two thirds of the catalogue this CMS is
          bound to has no media at all: an asset with nothing to play shows no
          preview, and "no button" must not read as "the feature is missing". */}
      {medias.length === 0 && (
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          No media yet. That is a valid draft and an invalid offer — the
          metadata usually arrives days before the stream URLs do, so nothing
          refuses it. It is the reason a row can be saved and still play
          nothing, and it is why there is nothing to preview here: a preview
          needs a stream URL to open.
        </Typography>
      )}

      {medias.map((media, index) => (
        <CollectionRow
          key={media.mediaId ?? `new-${index}`}
          title={
            media.name.trim() ||
            `${MEDIA_TYPE_LABELS[media.type]}${
              media.mediaId === null ? " (new)" : ` #${media.mediaId}`
            }`
          }
          removeLabel="Remove media"
          onRemove={() => onChange(medias.filter((_, i) => i !== index))}
        >
          <Stack spacing={2}>
            {/* The preview belongs HERE as well as on each stream, and burying
                it only in the stream rows was the mistake: a media is what an
                operator thinks they are checking ("does this title play?"),
                and the row it lives on is four scrolls down inside a section
                most people never open. This opens the rendition the estate
                actually plays - HLS first - and says so when there is none. */}
            <MediaPreviewHeader media={media} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <Field
                select
                label="Type"
                value={media.type}
                onChange={(e) =>
                  update(index, { type: e.target.value as MediaType })
                }
              >
                {MEDIA_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {MEDIA_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </Field>
              <Field
                label="Name"
                placeholder="e.g. UHD main"
                value={media.name}
                onChange={(e) => update(index, { name: e.target.value })}
              />
              <Field
                select
                label="Resolution"
                value={media.videoResolution}
                onChange={(e) =>
                  update(index, {
                    videoResolution: e.target.value as Resolution,
                  })
                }
              >
                {RESOLUTIONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Field>

              <Field
                label="Width (px)"
                value={media.videoWidth}
                onChange={(e) => update(index, { videoWidth: e.target.value })}
              />
              <Field
                label="Height (px)"
                value={media.videoHeight}
                onChange={(e) => update(index, { videoHeight: e.target.value })}
              />
              <Field
                label="Duration (milliseconds)"
                placeholder="0 for a live feed"
                value={media.durationMillis}
                onChange={(e) =>
                  update(index, { durationMillis: e.target.value })
                }
                helperText="Milliseconds here; the title's own Length is seconds."
              />

              <Field
                label="Spoken language"
                placeholder="sv"
                value={media.spokenLanguage}
                onChange={(e) =>
                  update(index, { spokenLanguage: e.target.value })
                }
              />
            </Box>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={4}
              sx={{ pt: 1 }}
            >
              <StringListField
                label="Audio languages"
                hint="One code per entry — lowercase, 2–3 letters."
                value={media.audioLanguages}
                placeholder="sv"
                onChange={(next) => update(index, { audioLanguages: next })}
              />
              <StringListField
                label="Subtitle languages"
                hint="One code per entry."
                value={media.subtitleLanguages}
                placeholder="en"
                onChange={(next) => update(index, { subtitleLanguages: next })}
              />
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={media.multiChannelAudio}
                      onChange={(e) =>
                        update(index, { multiChannelAudio: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12 }}>
                      Multi-channel audio
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={media.hardOfHearing}
                      onChange={(e) =>
                        update(index, { hardOfHearing: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12 }}>
                      Hard-of-hearing track
                    </Typography>
                  }
                />
              </Stack>
            </Stack>

            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: tokens.ink,
                  mb: 1,
                }}
              >
                Streams
              </Typography>
              <Typography
                sx={{ fontSize: 11, color: tokens.inkMuted, mb: 1.5 }}
              >
                The same feed in different packagings — HLS for one device
                family, DASH for another. The player picks; the catalogue
                records every option.
              </Typography>
              <Stack spacing={1.5}>
                {media.mediaResources.map((resource, resourceIndex) => (
                  <ResourceRow
                    key={resource.mediaResourceId ?? `new-${resourceIndex}`}
                    resource={resource}
                    index={resourceIndex}
                    siblings={media.mediaResources}
                    onChange={(patch) =>
                      update(index, {
                        mediaResources: media.mediaResources.map((r, i) =>
                          i === resourceIndex ? { ...r, ...patch } : r
                        ),
                      })
                    }
                    onRemove={() =>
                      update(index, {
                        mediaResources: media.mediaResources.filter(
                          (_, i) => i !== resourceIndex
                        ),
                      })
                    }
                  />
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start" }}
                  data-testid={`add-resource-${index}`}
                  onClick={() =>
                    update(index, {
                      mediaResources: [
                        ...media.mediaResources,
                        emptyResourceDraft(),
                      ],
                    })
                  }
                >
                  Add stream +
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CollectionRow>
      ))}

      <Button
        size="small"
        variant="outlined"
        sx={{ alignSelf: "flex-start" }}
        data-testid="add-media"
        onClick={() => onChange([...medias, emptyMediaDraft(defaultType)])}
      >
        Add media +
      </Button>
    </Stack>
  );
}
